import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { uploadedInvoiceService } from '../services/uploadedInvoiceService'
import type { UploadedInvoice } from '../types/invoice'
import {
  DocumentTextIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  CloudArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

interface UploadedInvoicesListProps {
  refreshTrigger: number
}

const UploadedInvoicesList = ({ refreshTrigger }: UploadedInvoicesListProps) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showSuccess, showError, showConfirm } = useNotification()
  const [uploadedInvoices, setUploadedInvoices] = useState<UploadedInvoice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editingInvoice, setEditingInvoice] = useState<UploadedInvoice | null>(null)
  const [editFormData, setEditFormData] = useState({
    title: '',
    clientName: '',
    amount: '',
    issueDate: '',
    dueDate: '',
    notes: '',
    status: 'draft' as UploadedInvoice['status'],
    paymentStatus: 'unpaid' as UploadedInvoice['paymentStatus'],
    paidAmount: '',
    paidDate: '',
    paymentMethod: '',
    paymentNotes: ''
  })

  useEffect(() => {
    if (user) {
      loadUploadedInvoices()
    }
  }, [user, refreshTrigger])

  const loadUploadedInvoices = async () => {
    if (!user) return

    setIsLoading(true)
    try {
      const result = await uploadedInvoiceService.getUploadedInvoices(user.id)
      if (result.success && result.invoices) {
        setUploadedInvoices(result.invoices)
      } else {
        console.error('Failed to load uploaded invoices:', result.error)
      }
    } catch (error) {
      console.error('Error loading uploaded invoices:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleViewInvoice = (invoice: UploadedInvoice) => {
    navigate(`/invoices/uploaded/${invoice.id}`)
  }

  const handleEditInvoice = (invoice: UploadedInvoice) => {
    setEditingInvoice(invoice)
    setEditFormData({
      title: invoice.title || '',
      clientName: invoice.clientName || '',
      amount: invoice.amount?.toString() || '',
      issueDate: invoice.issueDate || '',
      dueDate: invoice.dueDate || '',
      notes: invoice.notes || '',
      status: invoice.status,
      paymentStatus: invoice.paymentStatus,
      paidAmount: invoice.paidAmount?.toString() || '0',
      paidDate: invoice.paidDate || '',
      paymentMethod: invoice.paymentMethod || '',
      paymentNotes: invoice.paymentNotes || ''
    })
  }

  const handleSaveEdit = async () => {
    if (!editingInvoice) return

    try {
      const updates = {
        title: editFormData.title || undefined,
        clientName: editFormData.clientName || undefined,
        amount: editFormData.amount ? parseFloat(editFormData.amount) : undefined,
        issueDate: editFormData.issueDate || undefined,
        dueDate: editFormData.dueDate || undefined,
        notes: editFormData.notes || undefined
      }

      const result = await uploadedInvoiceService.updateUploadedInvoice(editingInvoice.id, updates)
      
      if (result.success) {
        // Update status if changed
        if (editFormData.status !== editingInvoice.status) {
          await uploadedInvoiceService.updateInvoiceStatus(editingInvoice.id, editFormData.status)
        }

        // Update payment info if changed
        const paymentData = {
          paymentStatus: editFormData.paymentStatus,
          paidAmount: editFormData.paidAmount ? parseFloat(editFormData.paidAmount) : 0,
          paidDate: editFormData.paidDate || undefined,
          paymentMethod: editFormData.paymentMethod || undefined,
          paymentNotes: editFormData.paymentNotes || undefined
        }
        await uploadedInvoiceService.updatePaymentInfo(editingInvoice.id, paymentData)

        showSuccess('Invoice Updated', 'Invoice details have been updated successfully.')
        setEditingInvoice(null)
        loadUploadedInvoices() // Refresh the list
      } else {
        showError('Update Failed', result.error || 'Failed to update invoice.')
      }
    } catch (error) {
      console.error('Error updating invoice:', error)
      showError('Update Failed', 'An unexpected error occurred.')
    }
  }

  const handleMarkAsPaid = async (invoice: UploadedInvoice) => {
    try {
      const result = await uploadedInvoiceService.markAsPaid(
        invoice.id, 
        invoice.amount, 
        'Manual', 
        'Marked as paid from uploaded invoices'
      )
      
      if (result.success) {
        showSuccess('Invoice Marked as Paid', 'The invoice has been marked as paid successfully.')
        loadUploadedInvoices() // Refresh the list
      } else {
        showError('Update Failed', result.error || 'Failed to mark invoice as paid.')
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error)
      showError('Update Failed', 'An unexpected error occurred.')
    }
  }

  const handleDeleteInvoice = (invoice: UploadedInvoice) => {
    showConfirm(
      'Delete Uploaded Invoice',
      `Are you sure you want to delete "${invoice.originalFileName}"? This action cannot be undone.`,
      async () => {
        try {
          const result = await uploadedInvoiceService.deleteUploadedInvoice(invoice.id)
          
          if (result.success) {
            showSuccess('Invoice Deleted', 'The uploaded invoice has been deleted successfully.')
            loadUploadedInvoices() // Refresh the list
          } else {
            showError('Delete Failed', result.error || 'Failed to delete invoice.')
          }
        } catch (error) {
          console.error('Error deleting invoice:', error)
          showError('Delete Failed', 'An unexpected error occurred.')
        }
      }
    )
  }

  const getStatusIcon = (status: UploadedInvoice['status']) => {
    switch (status) {
      case 'draft':
        return <PencilIcon className="w-4 h-4 text-gray-400" />
      case 'sent':
        return <ArrowUpIcon className="w-4 h-4 text-blue-400" />
      case 'viewed':
        return <EyeIcon className="w-4 h-4 text-purple-400" />
      case 'paid':
        return <CheckCircleIcon className="w-4 h-4 text-green-400" />
      case 'overdue':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-400" />
      case 'cancelled':
        return <XCircleIcon className="w-4 h-4 text-gray-400" />
      default:
        return <ClockIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusColor = (status: UploadedInvoice['status']) => {
    switch (status) {
      case 'draft':
        return 'text-gray-400 bg-gray-500/20'
      case 'sent':
        return 'text-blue-400 bg-blue-500/20'
      case 'viewed':
        return 'text-purple-400 bg-purple-500/20'
      case 'paid':
        return 'text-green-400 bg-green-500/20'
      case 'overdue':
        return 'text-red-400 bg-red-500/20'
      case 'cancelled':
        return 'text-gray-400 bg-gray-500/20'
      default:
        return 'text-gray-400 bg-gray-500/20'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (fileType: string) => {
    if (fileType === 'application/pdf') {
      return <DocumentTextIcon className="w-6 h-6 text-red-400" />
    }
    return <DocumentTextIcon className="w-6 h-6 text-blue-400" />
  }

  if (isLoading) {
    return (
      <div className="glass-mockup rounded-xl p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
          <span className="ml-3 text-gray-400">Loading uploaded invoices...</span>
        </div>
      </div>
    )
  }

  if (uploadedInvoices.length === 0) {
    return (
      <div className="glass-mockup rounded-xl p-8 text-center">
        <CloudArrowDownIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Uploaded Invoices</h3>
        <p className="text-gray-400">
          Upload your previous invoices to keep track of all your work history.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <AnimatePresence>
        {uploadedInvoices.map((invoice) => (
          <motion.div
            key={invoice.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="glass-mockup rounded-xl p-6"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                {getFileIcon(invoice.fileType)}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-medium text-white truncate">
                      {invoice.title || invoice.originalFileName}
                    </h3>
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                      {getStatusIcon(invoice.status)}
                      <span className="capitalize">{invoice.status}</span>
                    </div>
                    <span className="text-xs text-gray-400 bg-gray-500/20 px-2 py-1 rounded">
                      {formatFileSize(invoice.fileSize)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    {invoice.clientName && (
                      <div className="flex items-center space-x-2 text-gray-300">
                        <UserIcon className="w-4 h-4 text-gray-400" />
                        <span>{invoice.clientName}</span>
                      </div>
                    )}
                    
                    {invoice.amount && (
                      <div className="flex items-center space-x-2 text-gray-300">
                        <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
                        <span>{formatCurrency(invoice.amount)}</span>
                        {invoice.paymentStatus === 'paid' && (
                          <span className="text-green-400 text-xs">(Paid)</span>
                        )}
                      </div>
                    )}
                    
                    {invoice.issueDate && (
                      <div className="flex items-center space-x-2 text-gray-300">
                        <CalendarIcon className="w-4 h-4 text-gray-400" />
                        <span>{formatDate(invoice.issueDate)}</span>
                      </div>
                    )}
                  </div>
                  
                  {invoice.notes && (
                    <p className="text-gray-400 text-sm mt-2 line-clamp-2">
                      {invoice.notes}
                    </p>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-2">
                    Uploaded {formatDate(invoice.uploadedAt)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleViewInvoice(invoice)}
                  className="p-2 text-gray-400 hover:text-blue-400 transition-colors rounded-lg hover:bg-white/10"
                  title="View invoice"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
                
                {invoice.status !== 'paid' && invoice.amount && (
                  <button
                    onClick={() => handleMarkAsPaid(invoice)}
                    className="p-2 text-gray-400 hover:text-green-400 transition-colors rounded-lg hover:bg-white/10"
                    title="Mark as paid"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                  </button>
                )}
                
                <button
                  onClick={() => handleEditInvoice(invoice)}
                  className="p-2 text-gray-400 hover:text-yellow-400 transition-colors rounded-lg hover:bg-white/10"
                  title="Edit details"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => handleDeleteInvoice(invoice)}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors rounded-lg hover:bg-white/10"
                  title="Delete invoice"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Full Screen Edit Modal - FIXED VERSION */}
      <AnimatePresence>
        {editingInvoice && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
              onClick={() => setEditingInvoice(null)}
            />
            
            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-4 z-[9999] flex items-center justify-center"
            >
              <div className="w-full max-w-6xl max-h-full overflow-y-auto glass-mockup rounded-2xl">
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                  <h2 className="text-xl font-semibold text-white">Edit Invoice Details</h2>
                  <button
                    onClick={() => setEditingInvoice(null)}
                    className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Invoice Title
                        </label>
                        <input
                          type="text"
                          value={editFormData.title}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g., Wedding Photography Invoice"
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Client Name
                        </label>
                        <input
                          type="text"
                          value={editFormData.clientName}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, clientName: e.target.value }))}
                          placeholder="Client name"
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Amount
                        </label>
                        <input
                          type="number"
                          value={editFormData.amount}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, amount: e.target.value }))}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Status
                        </label>
                        <select
                          value={editFormData.status}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value as UploadedInvoice['status'] }))}
                          className="input-field"
                        >
                          <option value="draft">Draft</option>
                          <option value="sent">Sent</option>
                          <option value="viewed">Viewed</option>
                          <option value="paid">Paid</option>
                          <option value="overdue">Overdue</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Issue Date
                        </label>
                        <input
                          type="date"
                          value={editFormData.issueDate}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Due Date
                        </label>
                        <input
                          type="date"
                          value={editFormData.dueDate}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                          className="input-field"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Information */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Payment Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Payment Status
                        </label>
                        <select
                          value={editFormData.paymentStatus}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, paymentStatus: e.target.value as UploadedInvoice['paymentStatus'] }))}
                          className="input-field"
                        >
                          <option value="unpaid">Unpaid</option>
                          <option value="partial">Partial</option>
                          <option value="paid">Paid</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Paid Amount
                        </label>
                        <input
                          type="number"
                          value={editFormData.paidAmount}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, paidAmount: e.target.value }))}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Paid Date
                        </label>
                        <input
                          type="date"
                          value={editFormData.paidDate}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, paidDate: e.target.value }))}
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Payment Method
                        </label>
                        <input
                          type="text"
                          value={editFormData.paymentMethod}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                          placeholder="e.g., Bank Transfer, PayPal, Cash"
                          className="input-field"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={editFormData.notes}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional notes about this invoice..."
                      rows={3}
                      className="input-field resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Payment Notes
                    </label>
                    <textarea
                      value={editFormData.paymentNotes}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, paymentNotes: e.target.value }))}
                      placeholder="Notes about payment..."
                      rows={2}
                      className="input-field resize-none"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 p-6 border-t border-white/10">
                  <button
                    onClick={() => setEditingInvoice(null)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="btn-primary"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default UploadedInvoicesList 